// Mark'n'Post — Zod schema for flexible parcel form per brief v2 (2026-05-15).
import { z } from "zod";
import { isWhitelistedCountry, rejectRoute } from "./countries.js";
const countryCode = z
    .string()
    .min(2)
    .max(3)
    .refine(isWhitelistedCountry, "Страна не поддерживается.");
const phoneLocal = z
    .string()
    .trim()
    .min(4, "Слишком короткий номер.")
    .max(20)
    .regex(/^[0-9\- ()]+$/, "Только цифры, пробелы и -().");
const latinName = z
    .string()
    .trim()
    .min(2, "Минимум 2 символа.")
    .max(120)
    .regex(/^[A-Za-z .'\-]+$/, "Только латиница (a-z, пробел, дефис, апостроф).");
const passportFormat = z
    .string()
    .trim()
    .min(3, "Серия и номер паспорта обязательны.")
    .max(40);
const structuredAddressSchema = z.object({
    apartment: z.string().trim().max(50).optional().or(z.literal("")),
    building: z.string().trim().min(1).max(50),
    street: z.string().trim().min(1).max(200),
    city: z.string().trim().min(1).max(120),
    country: countryCode,
    zip: z.string().trim().min(1).max(20),
});
export const parcelRequestSchema = z
    .object({
    // Маршрут
    originCountry: countryCode,
    destinationCountry: countryCode,
    // Sender
    senderFullName: latinName,
    senderPhoneCode: z.string().regex(/^\+\d{1,4}$/, "Неверный код страны."),
    senderPhoneLocal: phoneLocal,
    senderPassport: passportFormat,
    senderAddressLine: z.string().trim().min(3).max(500).optional(),
    senderAddressStructured: structuredAddressSchema.optional(),
    declaredPriceEur: z
        .number({ invalid_type_error: "Введите стоимость числом." })
        .positive("Стоимость должна быть положительной.")
        .max(100_000),
    // Recipient — v2 3-flag model
    recipientFullName: latinName,
    recipientCountry: countryCode,
    recipientCity: z.string().trim().min(1).max(120),
    recipientMissingPhone: z.boolean().default(false),
    recipientMissingAddress: z.boolean().default(false),
    recipientPhoneCode: z.string().regex(/^\+\d{1,4}$/).optional().or(z.literal("")),
    recipientPhoneLocal: phoneLocal.optional().or(z.literal("")),
    recipientMessenger: z.string().trim().max(200).optional().or(z.literal("")),
    recipientApartment: z.string().trim().max(50).optional().or(z.literal("")),
    recipientBuilding: z.string().trim().max(50).optional().or(z.literal("")),
    recipientStreet: z.string().trim().max(200).optional().or(z.literal("")),
    recipientZip: z.string().trim().max(20).optional().or(z.literal("")),
    recipientEmail: z
        .string()
        .email("Проверьте email — похоже, опечатка. Пример: name@example.com")
        .optional()
        .or(z.literal("")),
    recipientGeoUrl: z.string().trim().max(500).optional().or(z.literal("")),
    // Pickup — v2 (selfDeliver inverted: false = курьер нужен)
    selfDeliver: z.boolean(),
    pickupGeoUrl: z.string().trim().max(500).optional().or(z.literal("")),
    pickupAddress: z.string().trim().max(500).optional().or(z.literal("")),
    pickupCity: z.string().trim().max(120).optional().or(z.literal("")),
    pickupFloor: z.number().int().min(-5).max(200).optional(),
    pickupHasElevator: z.boolean().optional(),
    pickupPhoneLocal: phoneLocal.optional().or(z.literal("")),
    pickupSameAsSender: z.boolean().default(false),
    pickupDay: z.string().trim().max(50).optional().or(z.literal("")), // free-text «26 сентября»
    pickupSlot: z.string().trim().max(50).optional().or(z.literal("")), // free-text «10:00–13:00»
    // Parcel — v2: только количество мест, без веса/габаритов (определяются на интейке)
    pieces: z.number().int().min(1).max(20),
    // Crate (обрешётка) — Блок 7 формы. Стоимость и вес обсуждаются менеджером,
    // в клиенте только фиксируем флаг + количество (≤ pieces).
    crateNeeded: z.boolean().default(false),
    crateCount: z.number().int().min(0).max(20).default(0),
    // Storage — v2: дата вместо числа дней
    storageNeeded: z.boolean().default(false),
    storageUntil: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "ГГГГ-ММ-ДД")
        .optional()
        .or(z.literal("")),
    // Template — save current submission as template
    saveTemplate: z.boolean().default(false),
    templateName: z.string().trim().max(60).optional().or(z.literal("")),
    templateEmoji: z.string().trim().max(8).optional().or(z.literal("")),
    // Confirmation
    prohibitedAck: z.literal(true, {
        errorMap: () => ({ message: "Подтвердите отсутствие запрещённых предметов." }),
    }),
    termsAck: z.literal(true, {
        errorMap: () => ({ message: "Подтвердите согласие с условиями." }),
    }),
})
    .superRefine((data, ctx) => {
    const r = rejectRoute(data.originCountry, data.destinationCountry);
    if (r) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["destinationCountry"],
            message: r.message,
        });
    }
    // Sender address: single-line для GE, structured для остальных
    if (data.originCountry === "GE") {
        if (!data.senderAddressLine || data.senderAddressLine.length < 3) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["senderAddressLine"],
                message: "Укажите адрес отправителя.",
            });
        }
    }
    else {
        if (!data.senderAddressStructured) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["senderAddressStructured"],
                message: "Заполните адрес отправителя.",
            });
        }
    }
    // Recipient phone — required unless missing-phone флаг
    if (!data.recipientMissingPhone) {
        if (!data.recipientPhoneCode || !data.recipientPhoneLocal) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["recipientPhoneLocal"],
                message: "Телефон получателя обязателен.",
            });
        }
        // Messenger required для RS/BG
        if ((data.destinationCountry === "RS" || data.destinationCountry === "BG") &&
            (!data.recipientMessenger || data.recipientMessenger.trim().length === 0)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["recipientMessenger"],
                message: "Для Сербии и Болгарии укажите Telegram/WhatsApp.",
            });
        }
    }
    // Recipient address — required unless missing-address флаг
    if (!data.recipientMissingAddress) {
        const required = [
            ["recipientBuilding", "Дом обязателен."],
            ["recipientStreet", "Улица обязательна."],
            ["recipientZip", "Индекс обязателен."],
        ];
        for (const [key, msg] of required) {
            const v = data[key];
            if (!v || (typeof v === "string" && v.trim().length === 0)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: msg });
            }
        }
    }
    // Pickup fields — required when !selfDeliver
    if (!data.selfDeliver) {
        if (!data.pickupGeoUrl || data.pickupGeoUrl.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["pickupGeoUrl"],
                message: "Ссылка на карту обязательна.",
            });
        }
        if (!data.pickupPhoneLocal) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["pickupPhoneLocal"],
                message: "Телефон для забора обязателен.",
            });
        }
        if (data.pickupFloor === undefined || data.pickupFloor === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["pickupFloor"],
                message: "Этаж обязателен.",
            });
        }
        if (data.pickupHasElevator === undefined || data.pickupHasElevator === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["pickupHasElevator"],
                message: "Укажите наличие лифта.",
            });
        }
        if (!data.pickupDay || !data.pickupSlot) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["pickupDay"],
                message: "Укажите день и слот забора.",
            });
        }
        if (data.originCountry === "GE") {
            const city = (data.pickupCity ?? "").toUpperCase();
            if (city !== "TBILISI" && city !== "BATUMI") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["pickupCity"],
                    message: "В Грузии забор работает в Тбилиси и Батуми.",
                });
            }
        }
    }
    if (data.recipientCountry !== data.destinationCountry) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["recipientCountry"],
            message: "Страна получателя должна совпадать с направлением.",
        });
    }
    // Storage — required date if storageNeeded
    if (data.storageNeeded && (!data.storageUntil || data.storageUntil.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["storageUntil"],
            message: "Укажите дату, до которой хранить.",
        });
    }
    // Template — name + emoji required if saveTemplate
    if (data.saveTemplate) {
        if (!data.templateName || data.templateName.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["templateName"],
                message: "Название шаблона обязательно.",
            });
        }
    }
    // Crate counter removed 2026-05-15 — менеджер уточнит. crateCount остаётся
    // в схеме (поле в БД) с default(0) для совместимости.
});
// ──────────────────────────────────────────────────────────────
// Patch schema для O3 (дозаполнение pre-order recipient).
// Все поля optional — посылаем только то, что пользователь дозаполнил.
// ──────────────────────────────────────────────────────────────
export const parcelRecipientPatchSchema = z.object({
    recipientPhoneCode: z.string().regex(/^\+\d{1,4}$/).optional(),
    recipientPhoneLocal: phoneLocal.optional(),
    recipientMessenger: z.string().trim().max(200).optional(),
    recipientApartment: z.string().trim().max(50).optional(),
    recipientBuilding: z.string().trim().max(50).optional(),
    recipientStreet: z.string().trim().max(200).optional(),
    recipientZip: z.string().trim().max(20).optional(),
    recipientEmail: z.string().email().optional(),
    recipientGeoUrl: z.string().trim().max(500).optional(),
});
// ──────────────────────────────────────────────────────────────
// Template snapshot — что сохраняем в ParcelTemplate.snapshot JSON.
// Только identity (brief v2 §1.5).
// ──────────────────────────────────────────────────────────────
export const parcelTemplateSnapshotSchema = z.object({
    originCountry: countryCode,
    destinationCountry: countryCode,
    sender: z.object({
        fullName: latinName,
        phoneCode: z.string(),
        phoneLocal: phoneLocal,
        passport: passportFormat,
        addressLine: z.string().optional(),
        addressStructured: structuredAddressSchema.optional(),
    }),
    recipient: z.object({
        fullName: latinName,
        country: countryCode,
        city: z.string().min(1).max(120),
        phoneCode: z.string().optional(),
        phoneLocal: z.string().optional(),
        messenger: z.string().optional(),
        apartment: z.string().optional(),
        building: z.string().optional(),
        street: z.string().optional(),
        zip: z.string().optional(),
        email: z.string().optional(),
        geoUrl: z.string().optional(),
    }),
    pickup: z
        .object({
        geoUrl: z.string().optional(),
        address: z.string().optional(),
        city: z.string().optional(),
        floor: z.number().optional(),
        hasElevator: z.boolean().optional(),
        phoneLocal: z.string().optional(),
    })
        .optional(),
});
export const parcelTemplateCreateSchema = z.object({
    name: z.string().trim().min(1).max(60),
    emoji: z.string().trim().max(8).optional(),
    snapshot: parcelTemplateSnapshotSchema,
});
export const parcelTemplateUpdateSchema = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    emoji: z.string().trim().max(8).optional(),
    snapshot: parcelTemplateSnapshotSchema.optional(),
});
//# sourceMappingURL=schemas.js.map